/**
 * Public static assets shipped in dist/client (CSS/JS/images under
 * /legacy-game-wiki, /assets, /vendor, …).
 *
 * vinext assetPrefix only rewrites /_next/static/* — these helpers cover
 * the rest so HTML/JSX/runtime loaders do not hit www → SPA HTML fallback.
 *
 * Live2D vendor scripts (/vendor/pixi*, /hero-portrait-layout.js) must also
 * use this helper; www.gamekee.com returns HTML for missing static paths.
 */

import { handleImgToWebp } from './image-webp';

function normalizePrefix(raw?: string): string {
  const value = raw?.trim();
  if (!value) return '';
  const withProtocol = value.startsWith('//') ? `https:${value}` : value;
  return withProtocol.replace(/\/$/, '');
}

function envTrim(name: keyof typeof ENV_SNAPSHOT): string | undefined {
  const value = ENV_SNAPSHOT[name]?.trim();
  return value || undefined;
}

/** 静态读取，避免 Vite define 把 process.env[name] 打成 {}[name] */
const ENV_SNAPSHOT = {
  NODE_ENV: process.env.NODE_ENV,
  NEXT_PUBLIC_ASSET_PREFIX: process.env.NEXT_PUBLIC_ASSET_PREFIX,
  ASSET_PREFIX: process.env.ASSET_PREFIX,
} as const;

/** Build-time CDN origin, e.g. https://cdnstatic.gamekee.com/wiki-v2 */
export function getStaticAssetPrefix(): string {
  return normalizePrefix(
    envTrim('NEXT_PUBLIC_ASSET_PREFIX') || envTrim('ASSET_PREFIX'),
  );
}

const PUBLIC_STATIC_ROOTS = [
  '/catalog-independent/',
  '/legacy-game-wiki/',
  '/assets/',
  '/vendor/',
] as const;

/** Root-level public files that also ship on CDN. */
const PUBLIC_STATIC_FILES = [
  '/hero-portrait-layout.js',
  '/hero-spine-player.js',
  '/hero-spine-player.html',
] as const;

/** Catalog source trees are pruned from the CDN package (OBS / local sync). */
const CDN_EXCLUDED_ASSET_PREFIXES = [
  '/assets/arcanatactics-source/',
  '/assets/lostsword-source/',
] as const;

function pathnameOnly(path: string): string {
  const query = path.indexOf('?');
  const hash = path.indexOf('#');
  let end = path.length;
  if (query >= 0) end = Math.min(end, query);
  if (hash >= 0) end = Math.min(end, hash);
  return path.slice(0, end);
}

function isPublicStaticPath(pathname: string): boolean {
  const clean = pathnameOnly(pathname);
  if (CDN_EXCLUDED_ASSET_PREFIXES.some((prefix) => clean.startsWith(prefix))) {
    return false;
  }
  if (PUBLIC_STATIC_FILES.some((file) => clean === file)) return true;
  return PUBLIC_STATIC_ROOTS.some((root) => clean.startsWith(root));
}

/**
 * Prefix a root-relative public asset path with the CDN origin when configured.
 * Absolute http(s)/protocol-relative/data URLs are returned unchanged.
 */
export function staticUrl(path: string): string {
  if (!path) return path;
  if (/^(?:https?:)?\/\//i.test(path) || path.startsWith('data:')) return path;

  const prefix = getStaticAssetPrefix();
  if (prefix && path.startsWith(prefix)) return path;

  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (!prefix || !isPublicStaticPath(normalized)) return normalized;
  return `${prefix}${normalized}`;
}

/**
 * 静态图片展示 URL：CDN 前缀 + WebP 转码（仅用于 <img> / background-image 等展示场景）。
 */
export function staticImageUrl(path: string): string {
  return handleImgToWebp(staticUrl(path));
}

/**
 * Rewrite root-relative public asset URLs embedded in HTML / markup strings.
 */
export function rewriteStaticUrlsInHtml(html: string): string {
  const prefix = getStaticAssetPrefix();
  if (!prefix) return html;

  return html.replace(
    /([("'=])(\/(?:legacy-game-wiki|assets|vendor)\/[^"'\)\s]*|\/hero-(?:portrait-layout|spine-player)[^"'\)\s]*)/g,
    (match, lead: string, pathname: string) => {
      if (!isPublicStaticPath(pathname)) return match;
      if (pathname.startsWith(prefix)) return match;
      return `${lead}${prefix}${pathname}`;
    },
  );
}
